/**
 * useRouteAuthEnabled Hook
 * Resolves query enabled-state from route auth metadata and auth readiness.
 */

'use client';

import {
  ApiRouteUnauthenticatedPolicy,
  ApiRouteVisibility,
  type ApiParamsInput,
  type ApiQueryInput,
} from '@cabal/shared/api-client';
import type { RouteContract } from '@cabal/shared/api-client/routes.contract';
import { useAuth } from '@/contexts/auth';
import { QUERY_STATUS } from '@/types/async-protocol';

interface UseRouteAuthEnabledInput {
  route: RouteContract<ApiParamsInput | undefined, ApiQueryInput | undefined, unknown, unknown>;
  enabled?: boolean;
}

interface ResolvedRouteAuth {
  visibility: ApiRouteVisibility;
  unauthenticatedPolicy: ApiRouteUnauthenticatedPolicy;
}

interface RouteAuthDecisionInput extends ResolvedRouteAuth {
  enabled: boolean;
  loading: boolean;
  hasUser: boolean;
}

interface RouteAuthDecision {
  enabled: boolean;
  shouldLogSkip: boolean;
}

interface RouteAuthDecisionRule {
  matches: (input: RouteAuthDecisionInput) => boolean;
  decision: RouteAuthDecision;
}

const ROUTE_AUTH_DECISION = {
  Enabled: { enabled: true, shouldLogSkip: false },
  Disabled: { enabled: false, shouldLogSkip: false },
  Skipped: { enabled: false, shouldLogSkip: true },
} as const satisfies Record<string, RouteAuthDecision>;

const ROUTE_AUTH_DECISION_RULES: readonly RouteAuthDecisionRule[] = [
  {
    matches: ({ enabled }) => !enabled,
    decision: ROUTE_AUTH_DECISION.Disabled,
  },
  {
    matches: ({ visibility }) => visibility === ApiRouteVisibility.Public,
    decision: ROUTE_AUTH_DECISION.Enabled,
  },
  {
    matches: ({ loading }) => loading,
    decision: ROUTE_AUTH_DECISION.Disabled,
  },
  {
    matches: ({ hasUser }) => hasUser,
    decision: ROUTE_AUTH_DECISION.Enabled,
  },
  {
    matches: ({ unauthenticatedPolicy }) => (
      unauthenticatedPolicy === ApiRouteUnauthenticatedPolicy.Skip
    ),
    decision: ROUTE_AUTH_DECISION.Skipped,
  },
];

const skippedRouteLogKeys = new Set<string>();

function resolveRouteAuth(input: {
  route: RouteContract<ApiParamsInput | undefined, ApiQueryInput | undefined, unknown, unknown>;
}): ResolvedRouteAuth {
  return {
    visibility: input.route.auth?.visibility ?? ApiRouteVisibility.Private,
    unauthenticatedPolicy: input.route.auth?.unauthenticatedPolicy ?? ApiRouteUnauthenticatedPolicy.Throw,
  };
}

function logRouteSkipOnce(input: {
  route: RouteContract<ApiParamsInput | undefined, ApiQueryInput | undefined, unknown, unknown>;
}): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  const key = `${input.route.method} ${input.route.routeTemplate}`;
  if (skippedRouteLogKeys.has(key)) {
    return;
  }
  skippedRouteLogKeys.add(key);
}

function resolveRouteAuthDecision(input: RouteAuthDecisionInput): RouteAuthDecision {
  const matchingRule = ROUTE_AUTH_DECISION_RULES.find((rule) => rule.matches(input));
  return matchingRule?.decision ?? ROUTE_AUTH_DECISION.Disabled;
}

export function useRouteAuthEnabled(input: UseRouteAuthEnabledInput): boolean {
  const auth = useAuth();
  const user = auth.data?.user ?? null;
  const loading = auth.status === QUERY_STATUS.Loading;
  const routeAuth = resolveRouteAuth({ route: input.route });
  const enabled = input.enabled ?? true;
  const decision = resolveRouteAuthDecision({
    ...routeAuth,
    enabled,
    loading,
    hasUser: Boolean(user),
  });

  if (decision.shouldLogSkip) {
    logRouteSkipOnce({ route: input.route });
  }

  return decision.enabled;
}
